import { useState, useEffect } from "react";
import {
  CheckCircle2,
  Minus,
  Plus,
  ShoppingBag,
  Trash2,
  Truck,
  ShieldCheck,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { useAuth } from "@project/hooks/useAuth";
import { post } from "@project/services/shared/api-client";
import { openRazorpayCheckout } from "@project/donate/components/razorpay-checkout";
import {
  PaymentVerificationOverlay,
  type PaymentVerificationStatus,
} from "@project/donate/components/payment-verification-overlay";
import { getApiErrorMessage } from "@project/lib/api-error";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { userDetailsService } from "@project/services/user/user-details.service";

export interface CartItem {
  id: string;
  name: string;
  category: string;
  price: number;
  unit: string;
  madeBy: string;
  quantity: number;
  variantId?: number;
  maxStock?: number;
}

interface GruhUdhyogOrderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cart: CartItem[];
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemoveItem: (id: string) => void;
  onClearCart: () => void;
}

export function GruhUdhyogOrderModal({
  open,
  onOpenChange,
  cart,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
}: GruhUdhyogOrderModalProps) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<"cart" | "details" | "success">("cart");
  const [loading, setLoading] = useState(false);
  const [backendOrderId, setBackendOrderId] = useState<number | null>(null);
  const [userDetailsId, setUserDetailsId] = useState<number | null>(null);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] =
    useState<PaymentVerificationStatus>("idle");
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

  // Form State
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  const [orderType, setOrderType] = useState<"home_delivery" | "pickup">(
    "home_delivery"
  );
  const [paymentMethod, setPaymentMethod] = useState<"COD" | "ONLINE">("COD");
  const [specialNote, setSpecialNote] = useState("");

  // Autofill user details when logged in
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (user && open) {
        setFullName(`${user.first_name ?? ""} ${user.last_name ?? ""}`.trim());
        setMobile(user.phone_number ?? "");
        setEmail(user.email ?? "");

        try {
          const details = await userDetailsService.get();
          if (details) {
            setUserDetailsId(details.id ?? null);
            if (details.pincode) {
              setPincode(details.pincode);
            }
            // Construct address line representation
            const addressParts = [
              details.address_line_1,
              details.address_line_2,
              details.landmark,
              details.city,
              details.state,
            ].filter(Boolean);
            if (addressParts.length > 0) {
              setAddress(addressParts.join(", "));
            }
          }
        } catch (err) {
          console.error(
            "Failed to load user details inside checkout modal:",
            err
          );
        }
      }
    };

    void fetchUserDetails();
  }, [user, open]);

  const subtotal = cart.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );
  const deliveryFee = orderType === "home_delivery" && subtotal < 500 ? 50 : 0;
  const grandTotal = subtotal + deliveryFee;

  const handleProceedToDetails = () => {
    if (cart.length === 0) {
      return;
    }
    if (!isAuthenticated) {
      toast.info("Please log in to complete your checkout.");
      navigate({
        to: "/login",
        search: { redirectTo: "/gruh-udhyog?bag=open" },
      });
      return;
    }
    setStep("details");
  };

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || !mobile || !address) {
      alert("Please fill in all required fields (Name, Mobile, Address).");
      return;
    }

    if (isAuthenticated && !userDetailsId) {
      toast.warning(
        "Please complete your profile details and address first to place an order."
      );
      return;
    }

    setLoading(true);
    try {
      const items = cart.map(item => ({
        variant: item.variantId,
        quantity: item.quantity,
      }));

      let finalUserDetailsId = userDetailsId;
      if (isAuthenticated && !finalUserDetailsId) {
        try {
          const savedDetails = await userDetailsService.save({
            address_line_1: address,
            pincode: pincode,
          });
          if (savedDetails?.id) {
            finalUserDetailsId = savedDetails.id;
            setUserDetailsId(savedDetails.id);
          }
        } catch (e) {
          console.error("Failed to auto-create user details record:", e);
        }
      }

      const payload = {
        user_details: finalUserDetailsId ?? 1, // backend user details ID
        payment_method: paymentMethod,
        items,
        full_name: fullName,
        mobile,
        email,
        address,
        pincode,
        order_type: orderType,
        special_note: specialNote,
      };

      const res = await post<{
        success?: boolean;
        message?: string;
        order_id?: number;
        razorpay_order_id?: string;
        amount?: number;
        key_id?: string;
        currency?: string;
        data?: {
          order_id: number;
          razorpay_order_id?: string;
          amount?: number;
          key_id?: string;
          currency?: string;
        };
      }>("/jivandhara/checkout/", payload);

      if (!res) {
        throw new Error("Failed to create order");
      }

      const orderData = res.data ?? res;
      if (!orderData?.order_id) {
        throw new Error(res.message ?? "Failed to create order");
      }

      setBackendOrderId(orderData.order_id);

      if (paymentMethod === "ONLINE") {
        if (!orderData.razorpay_order_id) {
          throw new Error(
            "Razorpay Order ID was not returned for online checkout"
          );
        }

        setIsPaymentProcessing(true);
        // Close the Radix Dialog BEFORE opening Razorpay so its iframe is not
        // blocked by Radix's aria-hidden / focus-trap.
        onOpenChange(false);

        // Small tick so Radix finishes unmounting its overlay
        await new Promise(resolve => window.setTimeout(resolve, 80));

        await openRazorpayCheckout({
          order_id: orderData.razorpay_order_id,
          amount: orderData.amount ?? grandTotal * 100,
          currency: orderData.currency ?? "INR",
          key: orderData.key_id,
          name: "Jivandhara Old Age Home",
          description: "Gruh Udhyog Order Payment",
          prefill: {
            name: fullName,
            email: email || undefined,
            contact: mobile,
          },
          onDismiss: () => {
            setIsPaymentProcessing(false);
            // Reopen the modal so the user can try again
            onOpenChange(true);
            toast.message("Payment cancelled. You can try again anytime.");
          },
          onSuccess: async response => {
            setVerificationOpen(true);
            setVerificationStatus("verifying");
            try {
              const verifyRes = await post<{
                success?: boolean;
                message?: string;
              }>("/jivandhara/verify-payment/", {
                order_id: orderData.order_id,
                razorpay_order_id: orderData.razorpay_order_id,
                razorpay_payment_id: String(response.razorpay_payment_id),
                razorpay_signature: String(response.razorpay_signature),
              });

              if (verifyRes && verifyRes.success !== false) {
                setVerificationStatus("success");
                setStep("success");
                onClearCart();
                setIsPaymentProcessing(false);
                // Reopen the modal to show the success state
                onOpenChange(true);
                window.setTimeout(() => {
                  setVerificationOpen(false);
                  setVerificationStatus("idle");
                }, 1600);
              } else {
                setVerificationOpen(false);
                setVerificationStatus("idle");
                setIsPaymentProcessing(false);
                onOpenChange(true);
                toast.error(
                  verifyRes?.message || "Payment verification failed."
                );
              }
            } catch (err) {
              setVerificationOpen(false);
              setVerificationStatus("idle");
              setIsPaymentProcessing(false);
              onOpenChange(true);
              toast.error(
                getApiErrorMessage(
                  err,
                  "Payment verification failed. Please contact support."
                )
              );
              console.error(err);
            }
          },
        });
      } else {
        // COD succeeds immediately
        setStep("success");
      }
    } catch (err: any) {
      alert(err?.message ?? "Failed to place order. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    if (isPaymentProcessing) {
      return;
    }
    setTimeout(() => {
      setStep("cart");
      if (step === "success") {
        onClearCart();
      }
    }, 300);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-0 sm:max-w-3xl sm:rounded-2xl">
          {/* Header Banner */}
          <div className="bg-hero-gradient relative overflow-hidden p-6 text-primary-foreground">
            <DialogHeader className="relative z-10 text-left">
              <div className="flex items-center justify-between">
                <span className="bg-gold/20 border-gold/30 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-gold">
                  <ShoppingBag className="h-3 w-3" />
                  Jivandhara Gruh Udhyog
                </span>
              </div>
              <DialogTitle className="mt-2 font-serif text-2xl font-bold text-white sm:text-3xl">
                {step === "cart" && "Your Shopping Bag"}
                {step === "details" && "Delivery & Contact Details"}
                {step === "success" && "Order Placed Successfully!"}
              </DialogTitle>
              <p className="text-primary-foreground/90 mt-1 text-xs">
                {step === "cart" &&
                  "Handcrafted pure products made with love by our elders & self-help groups."}
                {step === "details" &&
                  "Enter your shipping information to complete your order."}
                {step === "success" &&
                  "Thank you for supporting senior citizen artisans and Gau Seva!"}
              </p>
            </DialogHeader>
          </div>

          {/* STEP 1: CART ITEMS */}
          {step === "cart" && (
            <div className="p-6">
              {cart.length === 0 ? (
                <div className="py-12 text-center">
                  <ShoppingBag className="text-muted-foreground/40 mx-auto h-12 w-12" />
                  <h3 className="mt-4 font-serif text-lg font-bold text-foreground">
                    Your bag is empty
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Add products from our catalog to place an order.
                  </p>
                  <Button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    className="mt-6 rounded-xl bg-primary text-xs font-bold uppercase tracking-wider text-primary-foreground"
                  >
                    Explore Products
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="divide-y divide-border rounded-xl border border-border bg-card">
                    {cart.map(item => (
                      <div
                        key={item.id}
                        className="flex flex-col justify-between gap-4 p-4 sm:flex-row sm:items-center"
                      >
                        <div className="flex-1">
                          <span className="bg-primary/10 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                            {item.category}
                          </span>
                          <h4 className="mt-1 text-base font-semibold text-foreground">
                            {item.name}
                          </h4>
                          <p className="text-xs text-muted-foreground">
                            ₹{item.price} · {item.unit} ·{" "}
                            <span className="italic">by {item.madeBy}</span>
                          </p>
                        </div>

                        <div className="flex items-center justify-between gap-4 sm:justify-end">
                          {/* Quantity Selector */}
                          <div className="bg-secondary/30 flex items-center rounded-lg border border-border p-1">
                            <button
                              type="button"
                              onClick={() => onUpdateQuantity(item.id, -1)}
                              className="grid h-7 w-7 place-items-center rounded-md text-foreground transition hover:bg-background"
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="w-8 text-center text-xs font-bold">
                              {item.quantity}
                            </span>
                            <button
                              type="button"
                              disabled={item.quantity >= (item.maxStock ?? 99)}
                              onClick={() => onUpdateQuantity(item.id, 1)}
                              className="grid h-7 w-7 place-items-center rounded-md text-foreground transition hover:bg-background disabled:opacity-30"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>

                          {/* Item Total */}
                          <div className="w-20 text-right font-sans text-base font-bold text-foreground">
                            ₹{item.price * item.quantity}
                          </div>

                          {/* Remove */}
                          <button
                            type="button"
                            onClick={() => onRemoveItem(item.id)}
                            className="p-1 text-muted-foreground transition hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Price Breakdown */}
                  <div className="bg-secondary/20 space-y-2 rounded-xl border border-border p-4">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>
                        Subtotal ({cart.reduce((a, c) => a + c.quantity, 0)}{" "}
                        items)
                      </span>
                      <span className="font-semibold text-foreground">
                        ₹{subtotal}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>
                        Delivery Fee {subtotal >= 500 && "(Free above ₹500)"}
                      </span>
                      <span className="font-semibold text-foreground">
                        {deliveryFee === 0 ? "FREE" : `₹${deliveryFee}`}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-border pt-2 text-base font-bold text-foreground">
                      <span>Grand Total</span>
                      <span className="text-xl text-primary">
                        ₹{grandTotal}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      className="h-11 flex-1 rounded-xl text-xs font-bold uppercase tracking-wider"
                    >
                      Continue Shopping
                    </Button>
                    <Button
                      type="button"
                      onClick={handleProceedToDetails}
                      className="hover:bg-primary/90 h-11 flex-1 rounded-xl bg-primary text-xs font-bold uppercase tracking-wider text-primary-foreground"
                    >
                      Proceed to Checkout (₹{grandTotal})
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: SHIPPING DETAILS FORM */}
          {step === "details" && (
            <form onSubmit={handlePlaceOrder} className="space-y-5 p-6">
              {/* Delivery Option Toggle */}
              <div className="bg-secondary/40 grid grid-cols-2 gap-3 rounded-xl border border-border p-1">
                <button
                  type="button"
                  onClick={() => setOrderType("home_delivery")}
                  className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold transition ${
                    orderType === "home_delivery"
                      ? "border border-border bg-card text-primary shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Truck className="h-4 w-4" /> Home Delivery
                </button>
                <button
                  type="button"
                  onClick={() => setOrderType("pickup")}
                  className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold transition ${
                    orderType === "pickup"
                      ? "border border-border bg-card text-primary shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <ShieldCheck className="h-4 w-4" /> Self Pickup (Free)
                </button>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Full Name *</Label>
                  <Input
                    required
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">
                    Mobile Number *
                  </Label>
                  <Input
                    required
                    placeholder="+91 98250 00000"
                    value={mobile}
                    onChange={e => setMobile(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">
                    Email Address (Optional)
                  </Label>
                  <Input
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Pincode *</Label>
                  <Input
                    required
                    placeholder="380001"
                    value={pincode}
                    onChange={e => setPincode(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">
                  Delivery Address *
                </Label>
                <Textarea
                  required
                  rows={2}
                  placeholder="House / Flat No., Building, Street, Area, City"
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">
                  Special Instructions or Gift Note
                </Label>
                <Input
                  placeholder="Any special packing instructions, festival note..."
                  value={specialNote}
                  onChange={e => setSpecialNote(e.target.value)}
                  className="rounded-xl"
                />
              </div>

              {/* Payment Method Selector */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">
                  Payment Method *
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("COD")}
                    className={`flex h-11 items-center justify-center gap-2 rounded-xl border text-xs font-bold transition ${
                      paymentMethod === "COD"
                        ? "bg-primary/5 border-primary text-primary"
                        : "border-border bg-card text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Cash on Delivery (COD)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("ONLINE")}
                    className={`flex h-11 items-center justify-center gap-2 rounded-xl border text-xs font-bold transition ${
                      paymentMethod === "ONLINE"
                        ? "bg-primary/5 border-primary text-primary"
                        : "border-border bg-card text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    Online Payment (UPI/Card)
                  </button>
                </div>
              </div>

              {/* Order Summary Box */}
              <div className="border-primary/20 bg-primary/5 flex items-center justify-between rounded-xl border p-4">
                <div>
                  <span className="text-xs font-bold text-foreground">
                    Total Payable
                  </span>
                  <p className="text-[11px] text-muted-foreground">
                    {paymentMethod === "COD"
                      ? "Cash on Delivery / UPI on delivery"
                      : "Pay securely online via Razorpay"}
                  </p>
                </div>
                <div className="text-2xl font-bold text-primary">
                  ₹{grandTotal}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep("cart")}
                  className="h-11 flex-1 rounded-xl text-xs font-bold uppercase tracking-wider"
                >
                  Back to Cart
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="hover:bg-primary/90 h-11 flex-1 rounded-xl bg-primary text-xs font-bold uppercase tracking-wider text-primary-foreground"
                >
                  {loading
                    ? paymentMethod === "ONLINE"
                      ? "Processing Payment..."
                      : "Confirming Order..."
                    : paymentMethod === "ONLINE"
                      ? `Pay & Place Order (₹${grandTotal})`
                      : `Place Order (₹${grandTotal})`}
                </Button>
              </div>
            </form>
          )}

          {/* STEP 3: SUCCESS OVERLAY */}
          {step === "success" && (
            <div className="space-y-4 p-8 text-center">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-100 text-emerald-600">
                <CheckCircle2 className="h-10 w-10" />
              </div>

              <h3 className="font-serif text-2xl font-bold text-foreground">
                Order Placed Successfully!
              </h3>
              <p className="mx-auto max-w-md text-xs leading-relaxed text-muted-foreground">
                Thank you,{" "}
                <span className="font-semibold text-foreground">
                  {fullName}
                </span>
                ! Your order has been registered. Our team will prepare your
                fresh handmade batch and contact you at{" "}
                <span className="font-semibold text-foreground">{mobile}</span>{" "}
                for delivery coordination.
              </p>

              <div className="bg-secondary/20 mx-auto max-w-sm space-y-1.5 rounded-xl border border-border p-4 text-left text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Order ID:</span>
                  <span className="font-bold">
                    {backendOrderId
                      ? `JU-${backendOrderId}`
                      : "JU-" + Math.floor(100000 + Math.random() * 900000)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Paid/Due:</span>
                  <span className="font-bold text-primary">₹{grandTotal}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fulfillment:</span>
                  <span className="text-xs font-bold uppercase">
                    {orderType === "home_delivery"
                      ? "Home Delivery"
                      : "Self Pickup"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Method:</span>
                  <span className="text-[10px] font-bold uppercase">
                    {paymentMethod}
                  </span>
                </div>
              </div>

              <div className="pt-4">
                <Button
                  type="button"
                  onClick={handleClose}
                  className="hover:bg-primary/90 h-11 rounded-xl bg-primary px-8 text-xs font-bold uppercase tracking-wider text-primary-foreground"
                >
                  Done & Back to Store
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <PaymentVerificationOverlay
        open={verificationOpen}
        status={verificationStatus}
      />
    </>
  );
}
